import { supabase, isSupabaseConfigured } from "../../lib/supabaseClient";

function getPushFunctionUrl() {
  const base = process.env.REACT_APP_SUPABASE_URL || "";
  if (!base) return "";
  return `${base.replace(/\/$/, "")}/functions/v1/send-push`;
}

function getPushSecret() {
  return process.env.REACT_APP_PUSH_WEBHOOK_SECRET || "";
}

export function canSendPushFromClient() {
  if (process.env.REACT_APP_PUSH_CLIENT_DISPATCH === "false") {
    return false;
  }
  return Boolean(
    isSupabaseConfigured &&
      getPushFunctionUrl() &&
      getPushSecret() &&
      process.env.REACT_APP_VAPID_PUBLIC_KEY
  );
}

/**
 * Fallback client dispatch when DB pg_net trigger is not configured.
 * Primary delivery should come from database triggers after push_config is set.
 */
export async function dispatchPushNotification({
  recipientId,
  actorId,
  title,
  body,
  url = "/Messages",
  tag,
}) {
  if (!canSendPushFromClient() || !recipientId || !title || !body) {
    return { sent: 0, skipped: true };
  }

  if (actorId != null && String(actorId) === String(recipientId)) {
    return { sent: 0, skipped: true, reason: "actor_is_recipient" };
  }

  try {
    const response = await fetch(getPushFunctionUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-push-secret": getPushSecret(),
      },
      body: JSON.stringify({
        recipientId,
        actorId: actorId || null,
        title,
        body,
        url,
        tag,
      }),
      keepalive: true,
    });

    if (!response.ok) {
      const text = await response.text();
      console.warn("Push dispatch failed:", response.status, text);
      return { sent: 0, error: text };
    }

    return response.json();
  } catch (error) {
    console.warn("Push dispatch error:", error);
    return { sent: 0, error: String(error) };
  }
}

export async function getActorFirstName(actorId) {
  if (!supabase || !actorId) return "Someone";
  const { data } = await supabase
    .from("profiles")
    .select("first_name")
    .eq("id", actorId)
    .maybeSingle();
  const name = data?.first_name?.trim();
  return name || "Someone";
}

export async function notifyLikePush({ recipientId, actorId }) {
  const actorName = await getActorFirstName(actorId);
  return dispatchPushNotification({
    recipientId,
    actorId,
    title: "New like",
    body: `${actorName} liked you`,
    url: "/likes",
    tag: `like-${recipientId}-${actorId}`,
  });
}

export async function notifyMatchPush({ recipientId, actorId, conversationId }) {
  const actorName = await getActorFirstName(actorId);
  return dispatchPushNotification({
    recipientId,
    actorId,
    title: "New match!",
    body: `You matched with ${actorName}`,
    url: conversationId ? `/messages/${conversationId}` : "/Messages",
    tag: `match-${recipientId}-${conversationId || actorId}`,
  });
}

export async function notifyMessagePush({ recipientId, actorId, conversationId, content }) {
  const actorName = await getActorFirstName(actorId);
  const preview =
    content && content.length > 140 ? `${content.slice(0, 140)}…` : content || "New message";
  return dispatchPushNotification({
    recipientId,
    actorId,
    title: actorName,
    body: preview,
    url: `/messages/${conversationId}`,
    tag: `message-${conversationId}`,
  });
}
